
export type DownJawaban = 0 | 1 | 2 | null;

export type DownKategori = "ringan" | "sedang" | "berat";

export interface DownItems {
  frekuensi_nafas: DownJawaban;
  sianosis: DownJawaban;
  retraksi: DownJawaban;
  jalan_masuk_udara: DownJawaban;
  grunting: DownJawaban;
}

export type DownItemKey = keyof DownItems;

export interface DownOpsi {
  value: number;
  text: string;
}

export interface DownKomponen {
  key: DownItemKey;
  label: string;
  opsi: DownOpsi[];
}

function buatOpsi(daftar: string[]): DownOpsi[] {
  return daftar.map((text, value) => ({ value, text: `(${value}) ${text}` }));
}

export const DOWN_KOMPONEN: DownKomponen[] = [
  {
    key: "frekuensi_nafas",
    label: "Frekuensi Napas",
    opsi: buatOpsi(["< 60/menit", "60 - 80/menit", "> 80/menit"])
  },
  {
    key: "sianosis",
    label: "Sianosis",
    opsi: buatOpsi([
      "Tidak sianosis",
      "Sianosis hilang dengan O2",
      "Sianosis menetap walaupun diberi O2"
    ])
  },
  {
    key: "retraksi",
    label: "Retraksi",
    opsi: buatOpsi(["Tidak ada retraksi", "Retraksi ringan", "Retraksi berat"])
  },
  {
    key: "jalan_masuk_udara",
    label: "Air Entry",
    opsi: buatOpsi([
      "Udara masuk bilateral baik",
      "Penurunan ringan udara masuk",
      "Tidak ada udara masuk"
    ])
  },
  {
    key: "grunting",
    label: "Merintih",
    opsi: buatOpsi([
      "Tidak merintih",
      "Dapat didengar dengan stetoskop",
      "Dapat didengar tanpa alat bantu"
    ])
  }
];

export const DOWN_KEYS: DownItemKey[] = DOWN_KOMPONEN.map(komponen => komponen.key);

const LABEL_KATEGORI: Record<DownKategori, string> = {
  ringan: "Distress Napas Ringan; membutuhkan O2 nasal atau headbox",
  sedang: "Distres Napas Sedang; membutuhkan Nasal CPAP",
  berat:
    "Distres Napas Berat; Ancaman Gagal Napas; membutuhkan Intubasi (perlu diperiksa Analisa Gas Darah/AGD)"
};

const LABEL_KATEGORI_RINGKAS: Record<DownKategori, string> = {
  ringan: "Distress Napas Ringan",
  sedang: "Distres Napas Sedang",
  berat: "Distres Napas Berat"
};

const WARNA_KATEGORI: Record<DownKategori, string> = {
  ringan: "green darken-1",
  sedang: "amber darken-2",
  berat: "red darken-1"
};

export function downItemsKosong(): DownItems {
  const items = {} as DownItems;

  DOWN_KEYS.forEach(key => {
    items[key] = null;
  });

  return items;
}

export function downNormalizeItems(raw): DownItems {
  const src = raw && typeof raw === "object" ? raw : {};
  const items = {} as DownItems;

  DOWN_KEYS.forEach(key => {
    const val = src[key];

    if (val === null || val === undefined || val === "") {
      items[key] = null;
      return;
    }

    const angka = Number(val);

    items[key] =
      Number.isFinite(angka) && angka >= 0 && angka <= 2
        ? (Math.trunc(angka) as DownJawaban)
        : null;
  });

  return items;
}

export function downAdaIsian(items: DownItems): boolean {
  return DOWN_KEYS.some(key => items[key] !== null && items[key] !== undefined);
}

export function downLengkap(items: DownItems): boolean {
  return DOWN_KEYS.every(key => items[key] !== null && items[key] !== undefined);
}

export function downTerisi(items: DownItems): number {
  return DOWN_KEYS.filter(key => items[key] !== null && items[key] !== undefined).length;
}

export function downTotal(items: DownItems): number | null {
  if (!downLengkap(items)) {
    return null;
  }

  return DOWN_KEYS.reduce((total, key) => total + Number(items[key]), 0);
}

export function downKategori(total: number | null): DownKategori | null {
  if (total === null || total === undefined) {
    return null;
  }

  if (total > 7) {
    return "berat";
  }

  if (total >= 4) {
    return "sedang";
  }

  return "ringan";
}

export function downLabelKategori(kode: DownKategori | string | null): string {
  return kode && LABEL_KATEGORI[kode] ? LABEL_KATEGORI[kode] : "";
}

export function downLabelKategoriRingkas(kode: DownKategori | string | null): string {
  return kode && LABEL_KATEGORI_RINGKAS[kode] ? LABEL_KATEGORI_RINGKAS[kode] : "";
}

export function downWarna(kode: DownKategori | string | null): string {
  return kode && WARNA_KATEGORI[kode] ? WARNA_KATEGORI[kode] : "grey";
}
